import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';
import { layout } from 'styled-system';

import { BlurredBackground } from '@hivebrite/design-system/BlurredBackground';
import { Box } from '@hivebrite/design-system/Box';
import { Container } from '@hivebrite/design-system/Container';
import type { Theme, UseBreakpointProps } from '@hivebrite/design-system/Theme';
import {
  useTheme,
  responsive,
  useBreakpoint,
} from '@hivebrite/design-system/Theme';

import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import useDisplayBlurEffects from 'hb-react/shared/hooks/theme/useDisplayBlurEffects';
import type { PageHeaderTheme } from 'hb-react/shared/hooks/theme/usePageHeaderTheme';
import usePageHeaderTheme from 'hb-react/shared/hooks/theme/usePageHeaderTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import type {
  AppHeaderProps,
  AppHeaderVariant,
  GetAppHeaderVariant,
  ContentColProps,
} from './types';

const DESCRIPTION_CLAMP = {
  mid: { s: 8, m: 4, l: 4, xl: 4 },
  max: { s: 8, m: 5, l: 4, xl: 6 },
};

export const getAppHeaderVariant: GetAppHeaderVariant = (
  appHeaderProps,
  breakpoint,
) => {
  const {
    title,
    disableDescription,
    disableCoverPicture,
    description,
    controls,
    coverPicture,
    coverPictureNarrow,
    coverPictureWide,
  } = appHeaderProps ?? {};

  const showDescription = !disableDescription && description;
  const filteredControls = controls?.filter(Boolean);
  const showControls = filteredControls?.length > 0;

  const showContentCol =
    showDescription || showControls || (title && disableCoverPicture);

  const coverPictureUrls = {
    s: coverPicture,
    m: showContentCol ? coverPictureNarrow : coverPictureWide,
    l: showContentCol ? coverPicture : coverPictureWide,
    xl: showContentCol ? coverPicture : coverPictureWide,
  };
  const coverPictureUrl = coverPictureUrls[breakpoint] ?? coverPicture;
  const showPicture = !disableCoverPicture && coverPictureUrl;

  let variant: AppHeaderVariant = 'min';

  if (showControls || showDescription) {
    variant = showPicture ? 'max' : 'mid';
  } else if (showPicture) {
    variant = 'max';
  }

  return {
    variant,
    showDescription,
    filteredControls,
    showControls,
    showContentCol,
    coverPictureUrl,
    showPicture,
    isEmptyHeader: !showPicture && !showContentCol,
  };
};

export const useAppHeaderVariant = (appHeaderProps) => {
  const breakpoint = useBreakpoint();

  return getAppHeaderVariant(appHeaderProps, breakpoint);
};

const ContentCol = ({
  breakpoint,
  variant,
  title,
  description,
  filteredControls,
  showPicture,
  showDescription,
  showControls,
  isFullWidth,
  contentHeight,
  reduceBottomPadding,
}: ContentColProps) => {
  const isBottomPaddingReduced =
    reduceBottomPadding && ['min', 'mid'].includes(variant);
  const pageHeaderTheme = usePageHeaderTheme({ showPicture });
  const descriptionParagraphs = description?.split('\n');

  return (
    <ContentColRoot $showPicture={showPicture} $isFullWidth={isFullWidth}>
      <ContentBox
        height={contentHeight}
        $variant={variant}
        $isBottomPaddingReduced={isBottomPaddingReduced}
        $isFullWidth={isFullWidth}
        $showPicture={showPicture}
        $showDescription={showDescription}
        $showControls={showControls}
      >
        {title && (
          <Box font="h2-semibold" width="100%" as="h1">
            <Dotdotdot clamp={breakpoint === 's' ? 5 : 1} useNativeClamp>
              <span title={title}>{title}</span>
            </Dotdotdot>
          </Box>
        )}
        {showDescription && (
          <Box mt="6px" font="h4-regular" title={description}>
            <Dotdotdot
              clamp={DESCRIPTION_CLAMP[variant][breakpoint]}
              useNativeClamp
            >
              {descriptionParagraphs?.map((paragraph, index) => (
                <DescriptionParagraph key={index}>
                  {/* Non-breaking space ensures empty paragraphs have the proper height */}
                  {paragraph || '\u00A0'}
                </DescriptionParagraph>
              ))}
            </Dotdotdot>
          </Box>
        )}
        {showControls && (
          <ThemeProvider theme={pageHeaderTheme.themeName}>
            <Box mt={4} ml={-3}>
              {filteredControls.map((control, index) => (
                <Box key={index} ml={3} minWidth={0}>
                  {control}
                </Box>
              ))}
            </Box>
          </ThemeProvider>
        )}
      </ContentBox>
    </ContentColRoot>
  );
};

const PictureCol = ({
  breakpoint,
  contentHeight,
  coverPictureUrl,
  isFullWidth,
  showContentCol,
  title,
}) => (
  <PictureColRoot
    $isFullWidth={isFullWidth}
    $breakpoint={breakpoint}
    $showContentCol={showContentCol}
  >
    <Box position="relative">
      {showContentCol && breakpoint !== 's' ? (
        <CoverPicture
          $url={coverPictureUrl}
          height={contentHeight}
          data-testid="appheader-cover-picture"
        />
      ) : (
        <StaticCoverPicture
          src={coverPictureUrl}
          alt={__('Cover picture')}
          data-testid="appheader-cover-picture"
        />
      )}
      {!showContentCol && title && (
        <TitleBox>
          <Dotdotdot clamp={1} useNativeClamp>
            <span title={title}>{title}</span>
          </Dotdotdot>
        </TitleBox>
      )}
    </Box>
  </PictureColRoot>
);

// https://zeroheight.com/3d3aa52b0/p/21ce2e-navigation/t/42627f
const AppHeader = ({
  title,
  disableDescription,
  disableCoverPicture,
  description,
  controls,
  coverPicture,
  coverPictureNarrow,
  coverPictureWide,
  alignment = '12columns',
  reduceBottomPadding,
  fixedToLeft,
  dataTestId,
  hideShadow,
  ...otherProps
}: AppHeaderProps) => {
  const displayBlurEffects = useDisplayBlurEffects();
  const theme = useTheme();
  const breakpoint = useBreakpoint();

  const {
    variant,
    showDescription,
    filteredControls,
    showControls,
    showContentCol,
    coverPictureUrl,
    showPicture,
    isEmptyHeader,
  } = useAppHeaderVariant({
    title,
    disableDescription,
    disableCoverPicture,
    description,
    controls,
    coverPicture,
    coverPictureNarrow,
    coverPictureWide,
  });

  const pageHeaderTheme = usePageHeaderTheme({ showPicture });

  const scaleNumCols = {
    ...theme.grid.numCols,
    xl: alignment === '10columns' || variant === 'max' ? 10 : 12,
  };
  const numCols =
    (breakpoint === 's' ? scaleNumCols._ : scaleNumCols[breakpoint]) ?? 12;

  const isFullWidth = ['s', 'm'].includes(breakpoint);

  const CONTENT_HEIGHT = {
    min: 'auto',
    mid: 'auto',
    max: {
      s: 'auto',
      m: showContentCol ? 282 : 'auto',
      l: showContentCol ? 252 : 'auto',
      xl: showContentCol ? 310 : 'auto',
    },
  };
  const contentHeight = CONTENT_HEIGHT[variant];

  // Don't display an empty header
  if (isEmptyHeader) {
    return null;
  }

  return (
    <ThemeProvider
      theme={pageHeaderTheme.themeName}
      fontBrightness={pageHeaderTheme.fontBrightness}
    >
      <Wrapper
        $variant={variant}
        height={
          variant === 'max' && showContentCol
            ? { _: 'auto', m: 282, xl: 340 }
            : 'auto'
        }
        $isFullWidth={isFullWidth}
        $hideShadow={hideShadow}
        data-testid={dataTestId}
        $pageHeaderTheme={pageHeaderTheme}
        {...otherProps}
      >
        {showPicture && displayBlurEffects && (
          <BlurredBackground
            imageUrl={coverPictureUrl}
            display={{ _: 'none', l: 'block' }}
            height={{ _: 0, l: 282, xl: 340 }}
            width="100%"
            fadeTo={`var(--color-background, ${theme.colors.bodyBackgroundColor})`}
          />
        )}
        <Container
          width={isFullWidth && breakpoint !== 'm' ? '100%' : undefined}
          alignSelf={fixedToLeft ? 'flex-start' : 'center'}
          mx={fixedToLeft ? 0 : undefined}
        >
          {/* Using a single Grid made it impossible to add the box-shadow in XL with the 10columns alignment so we're using 2 Grids
        to maintain the proper column/space sizes while giving the InnerGrid the desired drop-shadow */}
          <OuterGrid>
            <Row>
              <OuterCol $numCols={numCols}>
                <InnerGrid
                  numCols={scaleNumCols}
                  $variant={variant}
                  $breakpoint={breakpoint}
                  $isFullWidth={isFullWidth}
                >
                  <Row>
                    {showContentCol && (
                      <ContentCol
                        breakpoint={breakpoint}
                        variant={variant}
                        title={title}
                        description={description}
                        filteredControls={filteredControls}
                        showPicture={showPicture}
                        showDescription={showDescription}
                        showControls={showControls}
                        isFullWidth={isFullWidth}
                        contentHeight={contentHeight}
                        reduceBottomPadding={reduceBottomPadding}
                      />
                    )}
                    {showPicture && (
                      <PictureCol
                        breakpoint={breakpoint}
                        contentHeight={contentHeight}
                        coverPictureUrl={coverPictureUrl}
                        isFullWidth={isFullWidth}
                        showContentCol={showContentCol}
                        title={title}
                      />
                    )}
                  </Row>
                </InnerGrid>
              </OuterCol>
            </Row>
          </OuterGrid>
        </Container>
      </Wrapper>
    </ThemeProvider>
  );
};

export default AppHeader;

const Wrapper = styled(Box).attrs<{
  $variant: AppHeaderVariant;
  $isFullWidth: boolean;
  $pageHeaderTheme: PageHeaderTheme;
}>(({ $variant, $isFullWidth, $pageHeaderTheme }) => ({
  flexDirection: 'column',
  width: '100%',
  bg:
    $variant === 'max' && !$isFullWidth
      ? 'transparent'
      : $pageHeaderTheme.backgroundColor,
}))<{
  $variant: AppHeaderVariant;
  $isFullWidth: boolean;
  $pageHeaderTheme: PageHeaderTheme;
  $hideShadow: boolean;
}>`
  position: relative;
  box-shadow: ${({ $isFullWidth, $hideShadow }) =>
    !$hideShadow && $isFullWidth ? '0 1px 6px 0 rgba(0, 0, 0, 0.1)' : 'none'};
`;

const OuterGrid = styled(Grid).attrs(() => ({
  rowGap: 0,
  flex: 1,
  maxWidth: 'calc(100% - 40px)',
}))``;

const OuterCol = styled(Col).attrs<{ $numCols: number }>(({ $numCols }) => ({
  offset: { _: 0, xl: $numCols === 10 ? 1 / 12 : 0 },
  width: { _: 1, xl: $numCols / 12 },
}))<{ $numCols: number }>``;

interface InnerGridProps {
  $variant: AppHeaderVariant;
  $breakpoint: UseBreakpointProps;
  $isFullWidth: boolean;
}

const InnerGrid = styled(Grid).attrs<InnerGridProps>(
  ({ $variant, $breakpoint }) => ({
    mt: $variant === 'max' && ['l', 'xl'].includes($breakpoint) ? '30px' : 0,
    mx: 0,
    rowGap: 0,
  }),
)<InnerGridProps>`
  ${({ theme }) => {
    const radius = `${theme.themeVariables.globalBorderRadius}px`;

    return responsive('l')`
  border-radius: ${radius};
  `;
  }}
  box-shadow: ${({ $variant, $isFullWidth }) =>
    $variant === 'max' && !$isFullWidth
      ? '0 1px 12px 2px rgba(0, 0, 0, 0.1)'
      : 'none'};
`;

const ContentColRoot = styled(Col).attrs<{
  $showPicture: boolean;
  $isFullWidth: boolean;
}>(({ $showPicture, $isFullWidth }) => ({
  width: {
    _: 1,
    m: $showPicture ? 5 / 12 : 9 / 12,
    l: $showPicture ? 5 / 12 : 8 / 12,
    xl: $showPicture ? 4 / 10 : 6 / 10,
  },
  growRight: true,
  growLeft: $isFullWidth,
}))<{
  $showPicture: boolean;
  $isFullWidth: boolean;
}>``;

const ContentBox = styled(Box).attrs<{
  theme: Theme;
  $variant: AppHeaderVariant;
  $isBottomPaddingReduced?: boolean;
  $isFullWidth: boolean;
  $showPicture: boolean;
  $showDescription: boolean;
  $showControls: boolean;
}>(
  ({
    theme,
    $variant,
    $isBottomPaddingReduced,
    $isFullWidth,
    $showPicture,
    $showDescription,
    $showControls,
  }) => {
    const bottomPaddingDefault = $isBottomPaddingReduced ? 3 : 6;

    return {
      flexDirection: 'column',
      pl: $variant === 'max' || $isFullWidth ? theme.grid.colGap : 0,
      pr: theme.grid.colGap,
      pt: {
        _: $showPicture || $showDescription || $showControls ? 4 : 6,
        m: 6,
      },
      pb: {
        _: $showPicture ? 4 : bottomPaddingDefault,
        m: bottomPaddingDefault,
      },
      bg: $showPicture ? theme.colors.white : undefined,
    };
  },
)<{
  theme: Theme;
  $variant: AppHeaderVariant;
  $isBottomPaddingReduced?: boolean;
  $isFullWidth: boolean;
  $showPicture: boolean;
  $showDescription: boolean;
  $showControls: boolean;
}>`
  ${({ theme }: { theme: Theme }) => {
    const radius = `${theme.themeVariables.globalBorderRadius}px`;

    return responsive('l')`
  border-top-left-radius: ${radius};
  border-bottom-left-radius: ${radius};
`;
  }}
`;

const PictureColRoot = styled(Col).attrs<{
  $isFullWidth: boolean;
  $breakpoint: UseBreakpointProps;
  $showContentCol: boolean;
}>(({ $isFullWidth, $breakpoint, $showContentCol }) => ({
  width: {
    _: 1,
    m: $showContentCol ? 7 / 12 : 1,
    xl: $showContentCol ? 6 / 10 : 1,
  },
  growRight: $isFullWidth ? 'pageBorder' : false,
  growLeft:
    $isFullWidth && !$showContentCol
      ? 'pageBorder'
      : $isFullWidth && $breakpoint === 's',
}))<{
  $isFullWidth: boolean;
  $breakpoint: UseBreakpointProps;
  $showContentCol: boolean;
}>``;

// Exported mainly for testing purposes
export const CoverPicture = styled(Box).attrs(() => ({
  width: '100%',
  flex: '0 0 auto',
}))<{ $url: string }>`
  background-image: url('${({ $url }) => $url}');
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  ${({ theme }: { theme: Theme }) => {
    const radius = `${theme.themeVariables.globalBorderRadius}px`;

    return responsive('l')`
    border-top-right-radius: ${radius};
    border-bottom-right-radius: ${radius};
`;
  }}
`;

// Exported mainly for testing purposes
// @ts-expect-error with ${layout} ; height can handle an object for breakpoint
export const StaticCoverPicture = styled.img.attrs(() => ({
  height: {
    s: '100%',
    m: '100%',
    l: 252,
    xl: 310,
  },
}))`
  ${layout}
  width: 100%;
  object-fit: cover;
  ${({ theme }) => {
    const radius = `${theme.themeVariables.globalBorderRadius}px`;

    return responsive('l')`
    border-radius: ${radius};
`;
  }}
`;

const TitleBox = styled(Box).attrs(({ theme }) => ({
  as: 'h1',
  font: 'h2-semibold',
  alignItems: 'center',
  top: { _: 5, l: 6 },
  pl: 6,
  pr: 4,
  height: { _: 48, l: 52 },
  mr: 6,
  position: 'absolute',
  bg: theme.colors.white,
}))`
  box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.1);
  border-top-right-radius: 2px;
  border-bottom-right-radius: 2px;
`;

const DescriptionParagraph = styled.p`
  margin: 0;
`;
